type ParseStrategy = "structured_subject_place" | "fallback_raw_query";

const hasText = (value?: string | null) => (value ?? "").trim().length > 0;

const normalizeWhitespace = (value: string) => value.trim().replace(/\s+/g, " ");

const normalizeToken = (value: string) => value.toLowerCase().trim();

const subjectAliases: Array<[string, string]> = [
  ["mathematics", "maths"],
  ["math", "maths"],
  ["maths", "maths"],
  ["english", "english"],
];

const matchAlias = (input: string, aliases: Array<[string, string]>) => {
  const normalized = normalizeToken(input);

  // Prefer longer aliases first so "a levels" is matched before "a"-like fragments.
  const ordered = [...aliases].sort((left, right) => right[0].length - left[0].length);
  for (const [alias, canonical] of ordered) {
    const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`(^|\\s)${escaped}(\\s|$)`, "i");
    if (pattern.test(normalized)) {
      return canonical;
    }
  }

  return null;
};

const extractIntentAndPlace = (input: string) => {
  const normalized = normalizeWhitespace(input);
  const lower = normalized.toLowerCase();

  for (const marker of [" near ", " in "]) {
    const markerIndex = lower.indexOf(marker);
    if (markerIndex >= 0) {
      const intent = normalized.slice(0, markerIndex).trim();
      const place = normalized.slice(markerIndex + marker.length).trim();

      return {
        intent,
        place,
      };
    }
  }

  return {
    intent: normalized,
    place: "",
  };
};

export type EducationNaturalQueryParseResult = {
  normalizedQuery: string;
  inferredPlace: string | null;
  inferredSubject: string | null;
  confidence: number;
  strategy: ParseStrategy;
};

export const parseNaturalEducationSearchQuery = (input: string): EducationNaturalQueryParseResult => {
  const normalizedQuery = normalizeWhitespace(input || "");
  if (!hasText(normalizedQuery)) {
    return {
      normalizedQuery: "",
      inferredPlace: null,
      inferredSubject: null,
      confidence: 0,
      strategy: "fallback_raw_query",
    };
  }

  const extracted = extractIntentAndPlace(normalizedQuery);
  const inferredPlace = hasText(extracted.place) ? extracted.place : null;
  const inferredSubject = matchAlias(extracted.intent, subjectAliases);

  const isConfident = Boolean(inferredPlace && inferredSubject);
  if (isConfident) {
    return {
      normalizedQuery,
      inferredPlace,
      inferredSubject,
      confidence: 0.9,
      strategy: "structured_subject_place",
    };
  }

  return {
    normalizedQuery,
    inferredPlace,
    inferredSubject,
    confidence: inferredPlace ? 0.35 : 0.2,
    strategy: "fallback_raw_query",
  };
};
